import React, { useEffect, useState } from 'react';

import { getInitialLoadIcons } from 'src/styles/font';
import {
  disableBackgroundScrolling,
  enableBackgroundScrolling
} from 'src/utils/common';
import { InitialLoadIconsSet } from 'src/utils/constants/IconsConstants';
import { B2BConstants } from 'src/utils/constants/stringConstants';
import { FeatureFlagConstants } from 'src/utils/constants/utilConstants';
import { isFeatureEnabled } from 'src/utils/RemoteConfig';

import { useAppStore } from 'src/hooks/useAppStore';
import SearchLanding from '../SearchLanding/SearchLanding';
import { confirmRemovePopupMixpanelEvent } from '../SearchMixpanelHelper';
import TopSearch from '../topSearch/TopSearch';
import * as style from './RecentSearchStyle';
import RemoveAllPopup from './RemoveAllPopup';

type RecentSearchPropType = {
  recentSearch: Array<string>;
  removeText: (currentSearch: string) => void;
  clearAllRecentSearchData: (e: React.MouseEvent) => void;
  searchByText: (searchText: string, e: React.MouseEvent) => void;
  showClearAllPopup: boolean;
  setClearAllPopup: (showClearAllPopup: boolean) => void;
  setSearchFocus: (searchFocus: boolean) => void;
  hideMenu: boolean;
  newSearchLandingRevamp: boolean;
  onBlur: () => void;
  setHideMenu?: (hideMenu: boolean) => void;
  setEnableSearchBox?: (enableSearchBox: boolean) => void;
};

interface EmotionStyles {
  name: string;
  styles: string;
}

const RecentSearch: React.FC<RecentSearchPropType> = ({
  recentSearch,
  removeText,
  clearAllRecentSearchData,
  searchByText,
  showClearAllPopup,
  setClearAllPopup,
  setSearchFocus,
  hideMenu,
  newSearchLandingRevamp,
  onBlur,
  setHideMenu,
  setEnableSearchBox
}) => {
  const { translations, appConfig, topSearch, remoteConfig } = useAppStore();
  const innerWidth = appConfig?.deviceWidth as number;
  const headerStrings = translations?.common;
  const shouldShowRecentSearch = isFeatureEnabled(
    remoteConfig,
    FeatureFlagConstants.FEATURE_RECENT_SEARCH
  );
  const shouldShowTopSearch = isFeatureEnabled(
    remoteConfig,
    FeatureFlagConstants.FEATURE_TOP_SEARCH
  );
  const { SEARCH_RECENT_SEARCHES, SEARCH_CLEAR_ALL_TEXT } = headerStrings || {};
  const recentSearchData = recentSearch || [];
  const [isViewAllClicked, setIsViewAllClicked] = useState(false);

  /* Clear recent search */
  const removeSearch = (e: React.MouseEvent, row: string) => {
    removeText(row);
    e.stopPropagation();
  };

  const clearAllRecentSearchHandler = () => {
    setClearAllPopup(true);
    confirmRemovePopupMixpanelEvent();
  };

  useEffect(() => {
    disableBackgroundScrolling();
    return enableBackgroundScrolling;
  }, []);

  const viewAllHandler = () => {
    setIsViewAllClicked(!isViewAllClicked);
  };

  // design components
  const noHistoryBlock = () => {
    return (
      <style.NoSearchHistory>
        <h3>No search history!</h3>
        <p>Try Searching for something.</p>
      </style.NoSearchHistory>
    );
  };

  // list components
  const listComponent = (filterRecentSearch: any) => {
    let viewAllCss: string | EmotionStyles = '';
    if (newSearchLandingRevamp) {
      viewAllCss = isViewAllClicked ? '' : style.viewAllCss;
    }
    return (
      <div css={style.allListContainer}>
        <div css={viewAllCss}>
          {shouldShowRecentSearch &&
            filterRecentSearch?.map((row: string) => {
              return (
                <>
                  <div css={style.recentSearchList} key={row} id={row}>
                    <style.Searchitem
                      data-testid="search-item"
                      onClick={(e) => searchByText(row, e)}>
                      {row}
                    </style.Searchitem>
                    <style.CloseIcon
                      data-testid="recent-search-close-icon"
                      css={getInitialLoadIcons(InitialLoadIconsSet.CLOSE)}
                      onClick={(e) => {
                        removeSearch(e, row);
                      }}></style.CloseIcon>
                  </div>
                </>
              );
            })}
        </div>
        {isViewAllClicked && newSearchLandingRevamp && (
          <style.RecentSearchClear onClick={clearAllRecentSearchHandler}>
            {SEARCH_CLEAR_ALL_TEXT}
          </style.RecentSearchClear>
        )}
      </div>
    );
  };

  const labelComponent = () => {
    return (
      <style.RecentSearchLbl data-testid="recent-searches-text">
        {SEARCH_RECENT_SEARCHES}
      </style.RecentSearchLbl>
    );
  };

  const oldRecentSearch = () => {
    const filterRecentSearch = recentSearchData?.slice(0, 5);
    return (
      <div
        css={style.recentSearchWrapper(
          topSearch?.length,
          recentSearchData?.length
        )}
        data-testid="recent-search">
        {shouldShowRecentSearch &&
        innerWidth < 1200 &&
        recentSearchData.length === 0 &&
        !topSearch.length ? (
          <style.SearchOverlyFocus>{noHistoryBlock()}</style.SearchOverlyFocus>
        ) : shouldShowRecentSearch && recentSearchData.length > 0 ? (
          <style.RecentSearchHeader>
            {labelComponent()}
            <style.RecentSearchClear
              onClick={clearAllRecentSearchHandler}
              data-testid="clear-search-text">
              {SEARCH_CLEAR_ALL_TEXT}
            </style.RecentSearchClear>
          </style.RecentSearchHeader>
        ) : null}
        <>{listComponent(filterRecentSearch)}</>
        {shouldShowTopSearch ? (
          <TopSearch hideMenu={hideMenu} onBlur={onBlur} />
        ) : null}
      </div>
    );
  };

  const newRecentSearch = () => {
    const filterRecentSearch = isViewAllClicked
      ? recentSearchData
      : recentSearchData?.slice(0, 5);
    return (
      <div
        css={style.recentSearchWrapper(
          topSearch?.length,
          recentSearchData?.length
        )}
        data-testid="recent-search">
        {shouldShowRecentSearch &&
        innerWidth < 1200 &&
        !recentSearchData?.length ? (
          <style.SearchOverlyFocus>{noHistoryBlock()}</style.SearchOverlyFocus>
        ) : shouldShowRecentSearch && recentSearchData?.length > 0 ? (
          <div>
            <style.RecentSearchHeader>
              {labelComponent()}
              <style.RecentSearchClear onClick={viewAllHandler}>
                {isViewAllClicked
                  ? B2BConstants.VIEW_LESS
                  : B2BConstants.VIEW_ALL}
              </style.RecentSearchClear>
            </style.RecentSearchHeader>
            <>{listComponent(filterRecentSearch)}</>
          </div>
        ) : null}
        {newSearchLandingRevamp ? <SearchLanding hideMenu={hideMenu} /> : null}
      </div>
    );
  };
  return (
    <>
      {newSearchLandingRevamp ? newRecentSearch() : oldRecentSearch()}
      <RemoveAllPopup
        showClearAllPopup={showClearAllPopup}
        setClearAllPopup={setClearAllPopup}
        clearAllRecentSearchData={clearAllRecentSearchData}
        setSearchFocus={setSearchFocus}
        setHideMenu={setHideMenu}
        setEnableSearchBox={setEnableSearchBox}
      />
    </>
  );
};

export default RecentSearch;
